import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  alpha
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import SecurityIcon from '@mui/icons-material/Security';
import { getCve, getCveNews, listCves } from 'api/cves';
import { getAccessToken } from 'api/authTokens';

const BRAND = '#22c55e';
const ORANGE = '#f97316';

const riskColor = (risk) => {
  if (risk === 'critical') return '#ef4444';
  if (risk === 'high') return '#f97316';
  if (risk === 'medium') return '#f59e0b';
  if (risk === 'low') return BRAND;
  return '#64748b';
};

const formatDate = (value) => {
  if (!value) return 'recent';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const normalizeNews = (item) => ({
  id: item.id,
  title: item.title || item.id,
  date: item.date || item.published_at || item.updated_at,
  summary: item.summary || item.description || '',
  product: item.product || (item.affected_products || [])[0] || '',
  vendor_project: item.vendor_project || '',
  required_action: item.required_action || '',
  risk: item.risk || (item.exploited ? 'critical' : item.severity) || 'info',
  exploited: item.exploited ?? true,
  source: item.source || 'CISA KEV'
});

export default function CveLiveTicker() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [details, setDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!getAccessToken()) return;
      try {
        setError('');
        const news = await getCveNews({ limit: 16 });
        const rows = (news.results || news || []).map(normalizeNews).filter((item) => item.id);
        if (mounted && rows.length) {
          setItems(rows);
          return;
        }
        const recent = await listCves({ days: 7, limit: 16 });
        if (mounted) setItems((recent.results || []).map(normalizeNews).filter((item) => item.id));
      } catch (err) {
        if (mounted) setError(err?.message || 'Unable to load CVE live news.');
      }
    };

    load();
    const timer = setInterval(load, 10 * 60 * 1000);
    return () => {
      mounted = false;
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadDetails = async () => {
      if (!selected?.id) return;
      setDetails(null);
      setLoadingDetails(true);
      try {
        const data = await getCve(selected.id);
        if (mounted) setDetails(data);
      } catch {
        if (mounted) setDetails(null);
      } finally {
        if (mounted) setLoadingDetails(false);
      }
    };
    loadDetails();
    return () => {
      mounted = false;
    };
  }, [selected?.id]);

  const tickerItems = useMemo(() => (items.length ? [...items, ...items] : []), [items]);

  if (error || !items.length) return null;

  const merged = details || selected || {};
  const affected = merged.affected_products || [];
  const references = merged.references || [];
  const exploits = merged.exploit_references || [];
  const kev = merged.kev || {};
  const risk = merged.risk || selected?.risk || 'info';
  const score = merged.score ?? null;

  return (
    <>
      <Box
        sx={{
          mb: 2,
          border: `1px solid ${alpha(BRAND, 0.34)}`,
          borderRadius: '14px',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, rgba(34,197,94,0.10), rgba(255,255,255,0.78) 42%, rgba(249,115,22,0.08))',
          boxShadow: '0 16px 42px rgba(15, 23, 42, 0.06)',
          "[data-pc-theme='dark'] &": {
            background: 'linear-gradient(135deg, rgba(34,197,94,0.12), rgba(9,15,25,0.84) 42%, rgba(249,115,22,0.10))'
          }
        }}
      >
        <Stack direction="row" alignItems="center" sx={{ minHeight: 44 }}>
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{
              px: 1.75,
              alignSelf: 'stretch',
              bgcolor: '#050505',
              color: BRAND,
              borderRight: `1px solid ${alpha(BRAND, 0.34)}`,
              flexShrink: 0,
              fontWeight: 800,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              fontSize: 12
            }}
          >
            <NewReleasesIcon sx={{ fontSize: 17, color: ORANGE }} />
            Live CVE
          </Stack>

          <Box sx={{ position: 'relative', flex: 1, overflow: 'hidden', py: 0.7 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1.25,
                whiteSpace: 'nowrap',
                // About 12 seconds per entry (roughly 45 px a second), so the speed stays readable however many entries there are.
                animation: `fuzzscan-cve-ticker ${Math.max(60, items.length * 12)}s linear infinite`,
                pr: 3,
                '@keyframes fuzzscan-cve-ticker': {
                  '0%': { transform: 'translateX(0)' },
                  '100%': { transform: 'translateX(-50%)' }
                },
                '&:hover': { animationPlayState: 'paused' }
              }}
            >
              {tickerItems.map((item, index) => {
                const itemRisk = item.risk || 'info';
                const color = riskColor(itemRisk);
                return (
                  <Box
                    key={`${item.id}-${index}`}
                    component="button"
                    type="button"
                    onClick={() => setSelected(item)}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.9,
                      border: 0,
                      bgcolor: 'transparent',
                      cursor: 'pointer',
                      color: 'var(--fz-text-primary)',
                      fontFamily: 'inherit',
                      p: 0,
                      '&:hover .cve-id': { color: BRAND, textDecoration: 'underline' }
                    }}
                  >
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, boxShadow: `0 0 0 4px ${alpha(color, 0.12)}` }} />
                    <Typography className="cve-id" component="span" sx={{ fontWeight: 800, fontSize: 13, color: 'var(--fz-text-primary)' }}>
                      {item.id}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{ fontSize: 13, color: 'text.secondary', maxWidth: 430, overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      {item.product || item.vendor_project
                        ? `${item.vendor_project ? `${item.vendor_project} ` : ''}${item.product}`
                        : item.summary || item.title}
                    </Typography>
                    <Chip
                      size="small"
                      label={item.exploited ? 'KEV' : itemRisk}
                      sx={{
                        height: 20,
                        bgcolor: alpha(color, 0.12),
                        color,
                        border: `1px solid ${alpha(color, 0.28)}`,
                        fontSize: 10,
                        fontWeight: 800
                      }}
                    />
                    <Typography component="span" sx={{ fontSize: 12, color: 'text.secondary', mr: 1.5 }}>
                      {formatDate(item.date)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Stack>
      </Box>

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1.5 }}>
          <Stack direction="row" alignItems="flex-start" gap={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: alpha(BRAND, 0.13),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <SecurityIcon sx={{ color: BRAND }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                <Typography variant="h5" sx={{ fontWeight: 900 }}>
                  {selected?.id}
                </Typography>
                <Chip
                  size="small"
                  label={risk}
                  sx={{ bgcolor: alpha(riskColor(risk), 0.12), color: riskColor(risk), fontWeight: 800, textTransform: 'uppercase' }}
                />
                {selected?.exploited || merged.exploited ? (
                  <Chip size="small" label="Known Exploited" sx={{ bgcolor: alpha('#ef4444', 0.12), color: '#ef4444', fontWeight: 800 }} />
                ) : null}
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {selected?.title || 'Recent vulnerability intelligence'}
              </Typography>
            </Box>
            <IconButton onClick={() => setSelected(null)} sx={{ ml: 'auto' }}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {loadingDetails ? (
            <Stack direction="row" alignItems="center" gap={1.5} sx={{ py: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Loading CVE details...
              </Typography>
            </Stack>
          ) : null}

          <Stack spacing={2}>
            <Box>
              <Typography variant="overline" sx={{ color: BRAND, fontWeight: 900 }}>
                Summary
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.7 }}>
                {merged.description || selected?.summary || 'No detailed summary is available for this item yet.'}
              </Typography>
            </Box>

            <Divider />

            <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5} flexWrap="wrap">
              <Chip label={`Published: ${formatDate(merged.published_at || selected?.date)}`} />
              <Chip label={`Updated: ${formatDate(merged.updated_at || selected?.date)}`} />
              {score !== null ? <Chip label={`CVSS: ${score}`} /> : null}
              {merged.cvss_version ? <Chip label={`CVSS v${merged.cvss_version}`} /> : null}
              {merged.vector ? <Chip label={merged.vector} sx={{ fontFamily: 'var(--fuzzscan-ui-font-mono)' }} /> : null}
            </Stack>

            {(selected?.vendor_project || kev.vendor_project || selected?.product || kev.product) && (
              <Box>
                <Typography variant="overline" sx={{ color: BRAND, fontWeight: 900 }}>
                  Affected Product
                </Typography>
                <Typography variant="body2">
                  {[selected?.vendor_project || kev.vendor_project, selected?.product || kev.product].filter(Boolean).join(' / ')}
                </Typography>
              </Box>
            )}

            {affected.length ? (
              <Box>
                <Typography variant="overline" sx={{ color: BRAND, fontWeight: 900 }}>
                  Affected CPE Products
                </Typography>
                <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                  {affected.slice(0, 12).map((product) => (
                    <Chip key={product} size="small" label={product} />
                  ))}
                </Stack>
              </Box>
            ) : null}

            {(selected?.required_action || kev.required_action) && (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                {selected?.required_action || kev.required_action}
              </Alert>
            )}

            {exploits.length || references.length ? (
              <Box>
                <Typography variant="overline" sx={{ color: BRAND, fontWeight: 900 }}>
                  References
                </Typography>
                <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                  {(exploits.length ? exploits.map((x) => x.url) : references).slice(0, 5).map((url) => (
                    <Stack key={url} direction="row" alignItems="center" gap={1}>
                      <OpenInNewIcon sx={{ fontSize: 15, color: BRAND }} />
                      <Tooltip title={url}>
                        <Typography
                          component="a"
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          variant="body2"
                          sx={{ color: BRAND, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {url}
                        </Typography>
                      </Tooltip>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            ) : null}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}
